import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Radio
} from '@mui/material';
import { Business as XeroIcon } from '@mui/icons-material';
import api from '../../config/api';

const XeroSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [connectorName, setConnectorName] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Parse URL parameters
  const urlParams = new URLSearchParams(location.search);
  const tempTokenId = urlParams.get('temp_token_id');

  useEffect(() => {
    if (!tempTokenId) {
      setError('Missing authentication token. Please restart the Xero connection process.');
      setLoading(false);
      return;
    }

    fetchOrganisations();
  }, [tempTokenId]);

  const fetchOrganisations = async () => {
    try {
      const response = await api.get('/api/connectors/xero/organisations', {
        params: { temp_token_id: tempTokenId }
      });

      if (response.data.success) {
        const orgs = response.data.organisations;
        setOrganisations(orgs);
        // Auto-select if only one org
        if (orgs.length === 1) {
          setSelectedOrg(orgs[0]);
          setConnectorName(response.data.suggested_name || `Xero - ${orgs[0].tenantName}`);
        }
      } else {
        setError(response.data.message || 'Failed to fetch Xero organisations');
      }
    } catch (err) {
      console.error('Error fetching organisations:', err);
      setError(err.response?.data?.message || 'Failed to fetch Xero organisations');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOrg = (org) => {
    setSelectedOrg(org);
    setConnectorName(`Xero - ${org.tenantName}`);
  };

  const handleCreateConnector = async () => {
    if (!selectedOrg) {
      setError('Please select an organisation');
      return;
    }
    if (!connectorName.trim()) {
      setError('Please enter a name for the connector');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const response = await api.post('/api/connectors/xero/create', {
        temp_token_id: tempTokenId,
        tenant_id: selectedOrg.tenantId,
        connector_name: connectorName.trim()
      });

      if (response.data.success) {
        navigate('/connectors', {
          state: {
            message: response.data.message,
            type: 'success'
          }
        });
      } else {
        setError(response.data.message || 'Failed to create Xero connector');
      }
    } catch (err) {
      console.error('Error creating connector:', err);
      setError(err.response?.data?.message || 'Failed to create Xero connector');
    } finally {
      setCreating(false);
    }
  };

  const handleReconnectExisting = async () => {
    if (!selectedOrg) return;

    setCreating(true);
    setError('');

    try {
      const response = await api.post('/api/connectors/xero/create', {
        temp_token_id: tempTokenId,
        tenant_id: selectedOrg.tenantId,
        connector_name: connectorName.trim(),
        create_new: false
      });

      if (response.data.success) {
        navigate('/connectors', {
          state: {
            message: response.data.message,
            type: 'success'
          }
        });
      } else {
        setError(response.data.message || 'Failed to reconnect Xero');
      }
    } catch (err) {
      console.error('Error reconnecting:', err);
      setError(err.response?.data?.message || 'Failed to reconnect Xero');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Xero organisations...</Typography>
      </Box>
    );
  }

  if (error && organisations.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/connectors')}>
          Back to Connectors
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <XeroIcon sx={{ fontSize: 48, color: '#13B5EA', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Select Xero Organisation
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {organisations.length > 1
            ? 'Your Xero account has access to multiple organisations. Please select the one you want to connect.'
            : 'Please confirm the Xero organisation details and connector name.'
          }
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Organisation List */}
      {organisations.length > 1 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Available Organisations
            </Typography>
            <List>
              {organisations.map((org) => (
                <ListItem key={org.tenantId} disablePadding>
                  <ListItemButton
                    selected={selectedOrg?.tenantId === org.tenantId}
                    onClick={() => handleSelectOrg(org)}
                  >
                    <ListItemIcon>
                      <Radio checked={selectedOrg?.tenantId === org.tenantId} />
                    </ListItemIcon>
                    <ListItemText
                      primary={org.tenantName}
                      secondary={`${org.tenantType || 'Organisation'} • ${org.tenantId}`}
                    />
                    {org.is_already_connected && (
                      <Chip label="Already Connected" color="info" variant="outlined" size="small" />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Selected Organisation Details */}
      {selectedOrg && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Organisation Details
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Organisation Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {selectedOrg.tenantName}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Tenant ID
              </Typography>
              <Typography variant="body1" sx={{
                fontFamily: 'monospace',
                backgroundColor: 'rgba(0,0,0,0.05)',
                padding: '4px 8px',
                borderRadius: '4px'
              }}>
                {selectedOrg.tenantId}
              </Typography>
            </Box>

            {selectedOrg.tenantType && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Type
                </Typography>
                <Typography variant="body1">
                  {selectedOrg.tenantType}
                </Typography>
              </Box>
            )}

            {selectedOrg.is_already_connected && (
              <Box sx={{ mt: 2 }}>
                <Chip
                  label="Already Connected"
                  color="info"
                  variant="outlined"
                  size="small"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  This organisation is already connected as: {selectedOrg.existing_connector_name}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Connector Settings */}
      {selectedOrg && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Connector Settings
            </Typography>

            <TextField
              fullWidth
              label="Connector Name"
              value={connectorName}
              onChange={(e) => setConnectorName(e.target.value)}
              helperText="Give this connection a memorable name"
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/connectors')}
                disabled={creating}
              >
                Cancel
              </Button>

              {selectedOrg.is_already_connected ? (
                <Button
                  variant="contained"
                  onClick={handleReconnectExisting}
                  disabled={creating || !connectorName.trim()}
                >
                  {creating ? <CircularProgress size={20} /> : 'Reconnect'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleCreateConnector}
                  disabled={creating || !connectorName.trim()}
                >
                  {creating ? <CircularProgress size={20} /> : 'Connect'}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default XeroSelection;
